import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import LoadingPlaceholder from 'components/loading-placeholder/LoadingPlaceholder';
import ProjectsTopSection from 'components/modules/project-management/ProjectsTopSection';
import GTable from 'components/tables/GTable';
import {
  activityLogColumns,
  projectListTableColumns
} from 'components/tables/ProjectListTable';
import { homepageBreadcrumbItems } from 'data/commonData';
import { useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  ActivityLog,
  GET_JOBS_QUERY,
  getAllActivityLogs,
  getActivityLogsByJobId
} from 'services/api/job';
import { Alert, Nav, Tab, Button } from 'react-bootstrap';

const ProjectListView = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('jobs');
  const [jobIdFilter, setJobIdFilter] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const jobId = searchParams.get('jobId');

    if (tabParam && (tabParam === 'jobs' || tabParam === 'activityLogs')) {
      setActiveTab(tabParam);
    }

    setJobIdFilter(jobId);
  }, [location]);

  const { data: jobs, isLoading } = useQuery('getJobs', GET_JOBS_QUERY, {
    staleTime: 3 * 60 * 60 * 1000
  });

  const {
    data: allActivityLogs,
    isLoading: isAllActivityLogsLoading,
    error: allActivityLogsError
  } = useQuery('getAllActivityLogs', getAllActivityLogs, {
    staleTime: 3 * 60 * 60 * 1000,
    enabled: activeTab === 'activityLogs' && !jobIdFilter
  });

  const {
    data: jobActivityLogs,
    isLoading: isJobActivityLogsLoading,
    error: jobActivityLogsError
  } = useQuery(
    ['getActivityLogsByJobId', jobIdFilter],
    () => (jobIdFilter ? getActivityLogsByJobId(jobIdFilter) : null),
    {
      staleTime: 3 * 60 * 60 * 1000,
      enabled: activeTab === 'activityLogs' && !!jobIdFilter
    }
  );

  const activityLogs = jobIdFilter ? jobActivityLogs : allActivityLogs;
  const isActivityLogsLoading = jobIdFilter
    ? isJobActivityLogsLoading
    : isAllActivityLogsLoading;
  const activityLogsError = jobIdFilter
    ? jobActivityLogsError
    : allActivityLogsError;

  const filteredJobs = jobs?.data?.filter(job => {
    const searchString = searchTerm.toLowerCase();
    return (
      job.title?.toLowerCase().includes(searchString) ||
      job.well?.toLowerCase().includes(searchString) ||
      job.jobNumber?.toLowerCase().includes(searchString) ||
      job.customer?.organizationName?.toLowerCase().includes(searchString) ||
      job.location?.location?.toLowerCase().includes(searchString)
    );
  });

  const filteredActivityLogs = activityLogs?.data?.filter(
    (log: ActivityLog) => {
      const searchString = searchTerm.toLowerCase();
      return (
        log.jobId.title?.toLowerCase().includes(searchString) ||
        log.jobId.jobNumber?.toLowerCase().includes(searchString) ||
        log.userId.firstName?.toLowerCase().includes(searchString) ||
        log.userId.lastName?.toLowerCase().includes(searchString)
      );
    }
  );

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const clearJobFilter = () => {
    setJobIdFilter(null);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('jobId');
    const newSearch = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';
    window.history.pushState({}, '', `${location.pathname}${newSearch}`);
  };

  const columns = useMemo(() => {
    return projectListTableColumns(true);
  }, []);

  const activityColumns = useMemo(() => {
    return activityLogColumns();
  }, []);

  return (
    <div>
      <PageBreadcrumb items={homepageBreadcrumbItems} />
      <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
        <h2 className="mb-0">
          <span className="me-3">Projects</span>{' '}
          <span className="fw-normal text-700">({jobs?.data?.length})</span>
        </h2>
        <Link className="btn btn-primary px-5" to="/home/apps/job/create">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add new job
        </Link>
      </div>
      <ProjectsTopSection
        activeView="list"
        onSearch={handleSearch}
        searchPlaceholder={`Search ${
          activeTab === 'jobs' ? 'Jobs' : 'Activity Logs'
        }`}
      />

      <Tab.Container
        id="project-tabs"
        activeKey={activeTab}
        onSelect={k => k && setActiveTab(k)}
      >
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="jobs">Jobs</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="activityLogs">Activity Logs</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="jobs">
            {isLoading ? (
              <LoadingPlaceholder />
            ) : (
              <GTable
                data={filteredJobs ?? []}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="activityLogs">
            {isActivityLogsLoading ? (
              <LoadingPlaceholder />
            ) : activityLogsError ? (
              <Alert variant="danger">Error loading activity logs</Alert>
            ) : (
              <>
                {jobIdFilter && (
                  <div className="mb-3 p-2 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>
                        Showing activity logs for job ID:{' '}
                        <strong>{jobIdFilter}</strong>
                      </span>
                      <Button variant="link" size="sm" onClick={clearJobFilter}>
                        Show all logs
                      </Button>
                    </div>
                  </div>
                )}
                <GTable
                  data={filteredActivityLogs ?? []}
                  columns={activityColumns}
                  isLoading={isActivityLogsLoading}
                />
              </>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default ProjectListView;
